import Icon from '@/components/ui/AppIcon';

interface WorkflowStep {
  number: string;
  title: string;
  description: string;
  icon: string;
  automationPoints: string[];
}

const WorkflowSection = () => {
  const workflowSteps: WorkflowStep[] = [
    {
      number: '01',
      title: 'Initial Contact',
      description: 'Customer reaches out through any channel—chat, email, phone, or social media',
      icon: 'ChatBubbleLeftRightIcon',
      automationPoints: [
        'Instant acknowledgment within 2 seconds',
        'Channel-agnostic unified inbox',
        'Automatic language detection',
        'Priority classification'
      ]
    },
    {
      number: '02',
      title: 'Intent Recognition',
      description: 'AI analyzes the inquiry to understand customer needs and context',
      icon: 'MagnifyingGlassIcon',
      automationPoints: [
        'Natural language processing',
        'Historical context retrieval',
        'Sentiment analysis',
        'Category classification'
      ]
    },
    {
      number: '03',
      title: 'Automated Resolution',
      description: 'AI handles routine inquiries with instant, accurate responses',
      icon: 'BoltIcon',
      automationPoints: [
        '70% of tickets resolved automatically',
        'Knowledge base integration',
        'Multi-step process automation',
        'Personalized responses'
      ]
    },
    {
      number: '04',
      title: 'Smart Escalation',
      description: 'Complex issues are intelligently routed to the right human agent',
      icon: 'ArrowTrendingUpIcon',
      automationPoints: [
        'Skill-based routing',
        'Context transfer to agent',
        'Priority queue management',
        'Workload balancing'
      ]
    },
    {
      number: '05',
      title: 'Agent Assistance',
      description: 'AI supports human agents with real-time suggestions and information',
      icon: 'UserGroupIcon',
      automationPoints: [
        'Response recommendations',
        'Knowledge article suggestions',
        'Customer history display',
        'Next-best-action guidance'
      ]
    },
    {
      number: '06',
      title: 'Resolution & Learning',
      description: 'Issue resolved with continuous AI learning from every interaction',
      icon: 'AcademicCapIcon',
      automationPoints: [
        'Satisfaction survey automation',
        'Pattern recognition',
        'Model improvement',
        'Performance analytics'
      ]
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="Cog6ToothIcon" size={20} variant="solid" />
            <span className="text-sm font-heading font-semibold">Support Workflow</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            Intelligent Support Automation Workflow
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our AI seamlessly integrates into your support process, automating routine tasks while ensuring complex issues receive expert human attention
          </p>
        </div>

        {/* Workflow Steps */}
        <div className="space-y-8">
          {workflowSteps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector Line */}
              {index < workflowSteps.length - 1 && (
                <div className="absolute left-8 top-24 w-0.5 h-full bg-gradient-to-b from-accent to-primary hidden lg:block" />
              )}

              <div className="bg-card rounded-2xl p-8 border border-border hover:border-accent/50 hover:shadow-elevated transition-all duration-300">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-16 h-16 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center mb-4">
                        <Icon name={step.icon as any} size={32} className="text-accent-foreground" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-xs font-heading font-bold">
                        {step.number}
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-heading font-bold text-foreground mb-3">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {step.description}
                    </p>

                    {/* Automation Points */}
                    <div className="bg-muted/30 rounded-xl p-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <Icon name="SparklesIcon" size={20} className="text-accent" variant="solid" />
                        <span className="text-sm font-heading font-semibold text-foreground">Automation Touchpoints</span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {step.automationPoints.map((point, pointIndex) => (
                          <div key={pointIndex} className="flex items-start space-x-2">
                            <Icon name="CheckCircleIcon" size={16} className="text-success mt-1 flex-shrink-0" variant="solid" />
                            <span className="text-sm text-foreground">{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-12 border border-primary/20">
          <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
            Ready to Transform Your Support Operations?
          </h3>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Let's discuss how our AI support system can be customized for your specific workflow and requirements
          </p>
          <button className="px-8 py-4 bg-accent text-accent-foreground font-heading font-semibold rounded-lg hover:bg-accent/90 hover:shadow-elevated transition-all duration-300 transform hover:-translate-y-0.5 inline-flex items-center space-x-2">
            <span>Schedule Workflow Analysis</span>
            <Icon name="ArrowRightIcon" size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default WorkflowSection;